import RequestService from './request';

/**
 * Hosturly's shop is WHMCS, so these are raw WHMCS payloads passed through by
 * the panel -- snake_case, and money arrives pre-formatted per currency.
 */
export interface HosturlyPrice {
    value: string;
    code: string;
    display_value: string;
    prefix: string;
    suffix: string;
}

export type HosturlyBillingCycle = 'monthly' | 'quarterly' | 'semiannually' | 'annually';

export interface HosturlyRecurringPrice {
    cycle: HosturlyBillingCycle;
    setup: HosturlyPrice;
    amount: HosturlyPrice;
}

export interface HosturlyPricing {
    is_free: boolean;
    recurring?: HosturlyRecurringPrice[];
}

export interface ComputeOption {
    id: number;
    name: string;
    description: string;
    is_domain_required: boolean;
    allow_multiple_quantities?: { is_supported: boolean; type: { code: number; display: string } };
    stock_control_enabled: boolean;
    stock_quantity: number | null;
    type: { name: string; display_name: string };
    order: number;
    group: { id: number; name: string };
    pricing: HosturlyPricing;
    // Annotated by the panel backend; not part of the WHMCS payload.
    category: 'vps' | 'dedicated';
    tier: 'premium' | 'budget' | null;
    vendor: 'amd' | 'intel' | null;
    // Dedicated only; null when the lookup failed for that product.
    locations: string[] | null;
}

export interface ConfigOptionChoice {
    name: string;
    value: number;
    sort_order: number;
    pricing: HosturlyPricing;
}

export interface ConfigOptionGroup {
    id: number;
    name: string;
    type: string;
    quantity_minimum: number | null;
    quantity_maximum: number | null;
    sort_order: number;
    options: ConfigOptionChoice[];
}

export interface CartTotals {
    recurring?: { cycle: HosturlyBillingCycle; amount: HosturlyPrice }[];
    subtotal?: HosturlyPrice;
    discounts?: { type: string; amount: HosturlyPrice }[];
    taxes?: { name: string; amount: HosturlyPrice }[];
    total?: HosturlyPrice;
}

export interface CartContents {
    items: Record<string, unknown>[];
    promotions: Record<string, unknown>[];
    totals: CartTotals;
}

export interface CreateCartItemPayload {
    product_id: number;
    billing_cycle: HosturlyBillingCycle;
    quantity: number;
    config_options: { id: number; value: number }[];
}

export interface CreateCartPayload {
    items: CreateCartItemPayload[];
    // Previous cart to rebuild in place, so the buyer's browser session on
    // the shop keeps following one cart.
    cart_id?: string;
}

class HosturlyService {
    computeOptions(): Promise<ComputeOption[]> {
        return RequestService.get('/hosturly/compute-options').then((res: { data: ComputeOption[] }) => res.data);
    }

    configOptions(productId: number): Promise<ConfigOptionGroup[]> {
        return RequestService.get(`/hosturly/products/${productId}/config-options`).then((res: { data: ConfigOptionGroup[] }) => res.data);
    }

    createCart(payload: CreateCartPayload): Promise<{ cart_id: string; contents: CartContents }> {
        return RequestService.post('/hosturly/cart', payload).then((res: { data: { cart_id: string; contents: CartContents } }) => res.data);
    }

    checkout(cartId: string): Promise<{ url: string }> {
        return RequestService.post(`/hosturly/cart/${cartId}/checkout`).then((res: { data: { url: string } }) => res.data);
    }
}

export default new HosturlyService();
